import { useId, type ReactNode } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { SheetDrawer } from './SheetDrawer';

// A bottom sheet: chart options, the HoverPanel sheet (K5). A grab handle, a
// title row with a 44 px close button, a body that scrolls, and an optional
// footer that stays put (the primary action). Safe areas included.
export interface BottomSheetProps {
    open: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string;
    /** Pinned under the scrolling body, e.g. one primary button. */
    footer?: ReactNode;
    maxHeight?: string;
    /** Space under the grab handle, in px (6 in the hover-panel mockup, 12 in the chart options one). */
    handleGap?: number;
    children: ReactNode;
}

export function BottomSheet({
    open,
    onClose,
    title,
    subtitle,
    footer,
    maxHeight = '85dvh',
    handleGap = 6,
    children,
}: BottomSheetProps) {
    const titleId = useId();
    return (
        <SheetDrawer
            open={open}
            onClose={onClose}
            labelledBy={titleId}
            paperSx={{ maxHeight }}
        >
            <Box
                aria-hidden
                sx={{
                    width: 36,
                    height: 4,
                    borderRadius: '2px',
                    bgcolor: theme => alpha(theme.palette.text.primary, 0.23),
                    alignSelf: 'center',
                    mt: 1,
                    mb: `${handleGap}px`,
                    flexShrink: 0,
                }}
            />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pl: 2.5,
                    pr: 1,
                    flexShrink: 0,
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id={titleId}
                        component="h2"
                        sx={{
                            fontSize: '17px',
                            fontWeight: 500,
                            lineHeight: 'normal',
                        }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            sx={{
                                fontSize: '13px',
                                lineHeight: 'normal',
                                mt: '2px',
                                color: 'text.secondary',
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    sx={{
                        width: 44,
                        height: 44,
                        color: theme => alpha(theme.palette.text.primary, 0.6),
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                }}
            >
                {children}
            </Box>
            {footer ? (
                <Box
                    sx={{
                        flexShrink: 0,
                        borderTop: 1,
                        borderColor: 'divider',
                        px: 2,
                        pt: 1.5,
                        pb: 'max(16px, env(safe-area-inset-bottom, 0px))',
                    }}
                >
                    {footer}
                </Box>
            ) : (
                <Box
                    aria-hidden
                    sx={{
                        flexShrink: 0,
                        height: 'env(safe-area-inset-bottom, 0px)',
                    }}
                />
            )}
        </SheetDrawer>
    );
}
